import { PalmettoCard, PalmettoComboBox, PalmettoIcon, PalmettoTextInput } from '@palmetto/dsm-components';
import { useCallback, useMemo, useState } from 'react';
import { Control, Controller, UseFormSetValue, useController, useWatch } from 'react-hook-form';

import { LabeledToggle } from './LabeledToggle';
import { MultiCriterionFormValue, RuleCardConfig, WelcomeCallStipRulesFormValues } from './types';

interface RuleCardProps {
  config: RuleCardConfig;
  control: Control<WelcomeCallStipRulesFormValues>;
  setValue: UseFormSetValue<WelcomeCallStipRulesFormValues>;
  disabled?: boolean;
}

// Mirrors the DSM input's own `ae.error` treatment (bg-input-bg-error border-border-error
// outline-border-error) so we can apply it manually via the pass-through `className` prop.
// PalmettoTextInput forwards `className` onto its bordered root, so this reproduces the real
// error look exactly. PalmettoComboBox does NOT forward className onto its bordered root (it
// lands on an inner Ark wrapper instead, confirmed by reading the compiled bundle) -- for
// combobox fields the red border can't be replicated this way, so only the message below turns
// red there. Necessary regardless: passing DSM's own `validationMessage` grows the input's own
// column (its message renders as an extra block below the box), which desyncs items-end
// alignment between the value and rate columns the moment either one errors.
const ERROR_INPUT_CLASSNAME = 'bg-input-bg-error border-border-error outline-border-error';

// A fixed-height slot (not min-height) so a hypothetically longer message in the future can
// never grow this and shift anything below it -- overflow-hidden is the backstop. All three
// current messages ('Enter a number', 'Enter a whole number 0-100', 'Select at least one
// option') were measured against the real font at every field width they can appear at and fit
// on one line with 100px+ to spare, so clipping should never actually trigger.
const FieldError = ({ message }: { message?: string }) => (
  <p data-testid="field-error" className="h-16 overflow-hidden text-xs text-text-error">
    {message}
  </p>
);

interface PerItemRateRowProps {
  itemValue: string;
  itemLabel: string;
  fieldKey: 'state' | 'preferredLanguage' | 'salesRepStatus';
  control: Control<WelcomeCallStipRulesFormValues>;
  defaultRate: string;
  disabled?: boolean;
  onRemove: (itemValue: string) => void;
}

const PerItemRateRow = ({
  itemValue,
  itemLabel,
  fieldKey,
  control,
  defaultRate,
  disabled,
  onRemove,
}: PerItemRateRowProps) => {
  const handleRemove = useCallback(() => onRemove(itemValue), [onRemove, itemValue]);

  const perItemRateRules = useMemo(
    () => ({
      validate: (value: string, formValues: WelcomeCallStipRulesFormValues) => {
        const criterion = formValues[fieldKey as keyof WelcomeCallStipRulesFormValues] as MultiCriterionFormValue;
        if (!criterion.enabled || value === '' || value === undefined) return true;
        if (!/^\d+$/.test(value)) return 'Enter a whole number 0-100';
        const rate = Number(value);
        return (rate >= 0 && rate <= 100) || 'Enter a whole number 0-100';
      },
    }),
    [fieldKey],
  );

  return (
    <div className="flex items-center gap-8 py-8">
      <span className="flex-1 text-sm text-text-primary">{itemLabel}</span>
      {fieldKey === 'state' && (
        <div className="w-[200px]">
          <Controller
            name={`state.templateIds.${itemValue}`}
            control={control}
            render={({ field }) => (
              <PalmettoTextInput
                id={`state-template-${itemValue}`}
                label="Recheck Template ID"
                value={field.value ?? ''}
                placeholder="Program template"
                onChange={field.onChange}
                onBlur={field.onBlur}
                disabled={disabled}
              />
            )}
          />
        </div>
      )}
      <div className="flex w-[140px] justify-center">
        <Controller
          name={`${fieldKey}.applicationRates.${itemValue}`}
          control={control}
          rules={perItemRateRules}
          render={({ field, fieldState }) => (
            <div className="flex flex-col gap-4">
              <div className="[&_input]:!w-[60px]">
                <PalmettoTextInput
                  id={`${fieldKey}-rate-${itemValue}`}
                  label="Apply Rate"
                  suffix="%"
                  type="number"
                  inputMode="numeric"
                  min={0}
                  max={100}
                  step={1}
                  value={field.value ?? ''}
                  placeholder={defaultRate}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  disabled={disabled}
                  className={fieldState.error ? ERROR_INPUT_CLASSNAME : undefined}
                />
              </div>
              <FieldError message={fieldState.error?.message} />
            </div>
          )}
        />
      </div>
      <div className="mt-[20px] flex w-8 items-center justify-center self-start">
        <button
          type="button"
          onClick={handleRemove}
          disabled={disabled}
          className="cursor-pointer border-none bg-transparent p-0 leading-none text-text-tertiary hover:text-text-primary disabled:cursor-not-allowed disabled:opacity-50"
          aria-label={`Remove ${itemLabel}`}
        >
          <PalmettoIcon name="trash" size="xs" />
        </button>
      </div>
    </div>
  );
};

export const RuleCard = ({ config, control, setValue, disabled }: RuleCardProps) => {
  const enabled = useWatch({ control, name: `${config.key}.enabled` });
  const defaultRate = useWatch({
    control,
    name: `${config.key}.applicationRate`,
  });

  const { field: valueField, fieldState: valueFieldState } = useController({
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    name: `${config.key}.value` as any,
    control,
    rules: {
      validate: (value: string | string[], formValues: WelcomeCallStipRulesFormValues) => {
        const criterion = formValues[config.key];
        if (!criterion.enabled) return true;
        if (config.kind === 'numeric') {
          const raw = value as string;
          return (raw.trim() !== '' && !Number.isNaN(Number(raw)) && Number(raw) >= 0) || 'Enter a number';
        }
        return (Array.isArray(value) && value.length > 0) || 'Select at least one option';
      },
    },
  });

  const selectedValues = useMemo(
    () => (Array.isArray(valueField.value) ? (valueField.value as string[]) : []),
    [valueField.value],
  );

  // For multi-with-rates: suppress DSM's selected-item tags (items live in the per-item table
  // instead) and disable re-adding by marking them disabled in the combobox.
  // For plain multi: keep selected: true so DSM renders the normal chip/checkmark UI and the
  // user can deselect via the combobox — there is no table to fall back on.
  const comboBoxItems = useMemo(
    () =>
      (config.options ?? []).map((opt) => {
        const isSelected = selectedValues.includes(opt.value);
        if (config.kind === 'multi-with-rates') {
          return Object.assign({}, opt, {
            selected: false,
            disabled: isSelected,
          });
        }
        return Object.assign({}, opt, { selected: isSelected });
      }),
    [config.options, config.kind, selectedValues],
  );

  const removeItem = useCallback(
    (itemToRemove: string) => {
      valueField.onChange((valueField.value as string[]).filter((existingValue) => existingValue !== itemToRemove));
      // Clear the stored rate so it doesn't reappear pre-filled if the item is re-added.
      // Empty string is the "no override" sentinel in buildApplicationRates (falls back to the
      // criterion's default rate), so setting it here is safe even before the row unmounts.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      setValue(`${config.key}.applicationRates.${itemToRemove}` as any, '');
      if (config.key === 'state') setValue(`state.templateIds.${itemToRemove}` as any, '');
    },
    [valueField, setValue, config.key],
  );

  // Increment to remount the combobox after each pick, which resets Ark's internal
  // selection state and prevents the selected tag from accumulating above the input.
  // DSM hardcodes multiple:true and doesn't expose value/selectionBehavior props,
  // so the key-remount is the only way to clear the internal selected-items state.
  const [comboBoxKey, setComboBoxKey] = useState(0);

  const handleComboBoxValueChange = useCallback(
    (details: { value: string[] }) => {
      const current = Array.isArray(valueField.value) ? (valueField.value as string[]) : [];
      const newItem = details.value[0];
      if (newItem && !current.includes(newItem)) {
        valueField.onChange([...current, newItem]);
      }
      setComboBoxKey((prevKey) => prevKey + 1);
    },
    [valueField],
  );

  const rateLabel =
    config.kind === 'multi-with-rates' ? `${config.label} Default Apply Rate` : `${config.label} Apply Rate`;

  const rateRules = useMemo(
    () => ({
      validate: (value: string, formValues: WelcomeCallStipRulesFormValues) => {
        const criterion = formValues[config.key];
        if (!criterion.enabled) return true;
        if (!/^\d+$/.test(value)) return 'Enter a whole number 0-100';
        const rate = Number(value);
        return (rate >= 0 && rate <= 100) || 'Enter a whole number 0-100';
      },
    }),
    [config.key],
  );

  const rateControl = (
    <div className="w-[260px]">
      <Controller
        name={`${config.key}.applicationRate`}
        control={control}
        rules={rateRules}
        render={({ field, fieldState }) => (
          <div className="flex flex-col gap-4">
            <PalmettoTextInput
              id={`${config.key}-rate`}
              label={rateLabel}
              suffix="%"
              type="number"
              inputMode="numeric"
              min={0}
              max={100}
              step={1}
              value={field.value}
              onChange={field.onChange}
              onBlur={field.onBlur}
              disabled={disabled}
              className={fieldState.error ? ERROR_INPUT_CLASSNAME : undefined}
            />
            <FieldError message={fieldState.error?.message} />
          </div>
        )}
      />
    </div>
  );

  const valueControl = (
    <div className={config.valueWidthClassName}>
      {config.kind === 'numeric' ? (
        <div className="flex flex-col gap-4">
          <PalmettoTextInput
            id={`${config.key}-value`}
            label={config.label}
            prefix={config.prefix}
            suffix={config.suffix}
            type="number"
            inputMode="decimal"
            min={0}
            step="any"
            value={valueField.value as string}
            onChange={valueField.onChange}
            onBlur={valueField.onBlur}
            disabled={disabled}
            className={valueFieldState.error ? ERROR_INPUT_CLASSNAME : undefined}
          />
          <FieldError message={valueFieldState.error?.message} />
        </div>
      ) : (
        <div className="flex flex-col gap-4">
          <PalmettoComboBox
            key={comboBoxKey}
            id={`${config.key}-value`}
            label={config.label}
            maxMenuHeight={320}
            items={comboBoxItems}
            onValueChange={handleComboBoxValueChange}
            disabled={disabled}
          />
          <FieldError message={valueFieldState.error?.message} />
        </div>
      )}
    </div>
  );

  const perItemRateControls =
    config.kind === 'multi-with-rates' && selectedValues.length > 0 ? (
      <div className="flex flex-col">
        <div className="flex items-center gap-8 border-b border-border-default pb-4 text-xs font-medium text-text-secondary">
          <span className="flex-1">{config.label}</span>
          {config.key === 'state' && <span className="w-[200px] text-center">Template ID Override</span>}
          <span className="w-[140px] text-center">Apply Rate</span>
          <span className="w-8" />
        </div>
        {selectedValues.map((itemValue) => {
          const itemLabel = config.options?.find((opt) => opt.value === itemValue)?.label ?? itemValue;
          return (
            <div key={itemValue} className="border-b border-border-default last:border-b-0">
              <PerItemRateRow
                itemValue={itemValue}
                itemLabel={itemLabel}
                fieldKey={config.key as 'state' | 'preferredLanguage' | 'salesRepStatus'}
                control={control}
                defaultRate={defaultRate}
                disabled={disabled}
                onRemove={removeItem}
              />
            </div>
          );
        })}
      </div>
    ) : null;

  // The combobox listbox positions absolutely inside the card (DSM does not portal it); the card's baked-in overflow-hidden clips it without this className. The trailing ! is required because DSM's own overflow-hidden otherwise wins the cascade.
  return (
    <PalmettoCard className="overflow-visible!">
      {/* No p-16 here: PalmettoCard's own internal wrapper already applies p-16/p-24
          padding, so adding more here was stacking double padding on every side. */}
      <div className="flex flex-col gap-8">
        <div className="flex items-center justify-between">
          <h3 className="text-base font-bold">{config.label}</h3>
          <Controller
            name={`${config.key}.enabled`}
            control={control}
            render={({ field }) => (
              <LabeledToggle
                id={`${config.key}-enabled`}
                label={field.value ? 'Enabled' : 'Disabled'}
                ariaLabel={`Enable ${config.label} rule`}
                checked={field.value}
                onChange={field.onChange}
                disabled={disabled}
              />
            )}
          />
        </div>

        {!enabled ? (
          <p className="text-sm text-text-secondary italic">This criterion is disabled and will not be evaluated.</p>
        ) : (
          <>
            <div className="flex flex-wrap items-end gap-16">
              {valueControl}
              {rateControl}
            </div>
            {perItemRateControls}
          </>
        )}

        {enabled && (
          <div className="flex flex-col gap-4">
            <p className="text-xs text-text-secondary">{config.conditionHelperText}</p>
            {config.kind === 'numeric' && (
              <p className="text-xs text-text-tertiary">
                Greater than or equal to. This operator is fixed and cannot be changed.
              </p>
            )}
          </div>
        )}
      </div>
    </PalmettoCard>
  );
};
